import { EarningsItemWithEntity, EarningsPeriod } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { mapEntity } from '@api/mappers/mapEntity';
import {
  type AppTableFeatures,
  appTableFeatures,
} from '@design/components/table';
import { Row, useTable } from '@tanstack/react-table';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEarningsData } from '@terminal/react-query-hooks/earnings/useEarningsData';
import { useEarningsInsightsByEarningsId } from '@terminal/react-query-hooks/insights/useEarningsInsightsByEarningsId';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { EarningsDrawer } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import { getColumns, getReducedColumns } from '../../utils/tableUtils';
import {
  EARNINGS_COLUMN_ID,
  QNA_COLUMN_ID,
  SELECTED_COLUMN_ID,
} from '../../utils/utils';
import { EarningsPriceMovementContent } from '../earnings-price-movement-content/EarningsPriceMovementContent';
import { EarningsTable } from '../earnings-table/EarningsTable';
import { getReportingPeriodLabel } from '../reporting-dates/utils';
import styles from './EarningsTableWrapper.module.scss';

interface EarningsTableWrapperProps {
  entity: Entity;
  isHistoryTab?: boolean;
}

export const EarningsTableWrapper = ({
  entity,
  isHistoryTab = false,
}: EarningsTableWrapperProps) => {
  const { t } = useTranslation(['widget']);
  const { isError, isRefetchError, error } = useEarningsData(entity.tag);
  const {
    activeSortingState,
    rowSelection,
    isViewSelectedOnly,
    updateRowSelection,
    getIsAllRowsSelected,
    getIsSomeRowsSelected,
    toggleAllRowsSelection,
    getIsRowManualChanged,
    getIsRowMatchingFilters,
    earningsData,
    earningExchange,
    filterGroups,
    resetAllFilterOptions,
  } = useEarningsStore();

  const { earningsInsightByEarningsId, isEarningsInsightsFetching } =
    useEarningsInsightsByEarningsId({
      earningsIds: earningsData.map(earning => earning.id),
    });

  const [drawerData, setDrawerData] = useState<
    EarningsItemWithEntity | undefined
  >();

  const { deleteQueryParam, setQueryParams } = useQueryParams();
  const { openDrawer: openInsightDrawer } = useEarningsInsightDrawer();
  const { trackEarningsRowClicked } = usePosthogEvents();

  const hasActiveFilters =
    !!filterGroups.EARNINGS_FILTERS?.activeFilterOptions.length;
  const shouldReduceColumns = isHistoryTab && hasActiveFilters;

  const toDrawerData = (
    row: Row<AppTableFeatures, EarningsPeriod>
  ): EarningsItemWithEntity => ({
    ...row.original,
    entity_tag: entity.tag,
    entity: mapEntity(entity),
    exchange: earningExchange,
  });

  const onRowClick = (row: Row<AppTableFeatures, EarningsPeriod>) => {
    trackEarningsRowClicked(
      POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      getReportingPeriodLabel(row.original),
      entity.tag
    );
    setDrawerData(toDrawerData(row));

    const insight = earningsInsightByEarningsId.get(row.original.id);
    if (insight) {
      openInsightDrawer({
        entityTag: insight.entity_tag,
        earningsId: insight.earnings_id,
        subtab:
          insight.type === 'preview' ? SubTabsType.Preview : SubTabsType.Recap,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    } else {
      setQueryParams(
        {
          [queryKeys.activeDrawer]: queryValues.earningsDrawer,
        },
        { replace: true }
      );
    }
  };

  const onDrawerClose = () => {
    deleteQueryParam([queryKeys.activeDrawer], { replace: true });
    setDrawerData(undefined);
  };

  const columns = getColumns({
    getIsAllRowsSelected,
    getIsSomeRowsSelected,
    toggleAllRowsSelection,
    updateRowSelection,
    rowSelection,
    getIsRowManualChanged,
    getIsRowMatchingFilters,
    earningsData,
    earningsInsightByEarningsId,
    isEarningsInsightsFetching,
    isNewEarningsTabEnabled: isHistoryTab,
  });

  const table = useTable({
    features: appTableFeatures,
    data: earningsData,
    columns: shouldReduceColumns ? getReducedColumns(columns) : columns,
    enableMultiSort: false,
    enableRowSelection: true,
    enableRowRangeSelection: false,
    state: {
      sorting: activeSortingState,
      rowSelection,
      columnPinning: {
        start: [SELECTED_COLUMN_ID, EARNINGS_COLUMN_ID, QNA_COLUMN_ID],
        end: [],
      },
    },
    getRowId: data => data.reporting_date,
    initialState: {
      pagination: { pageIndex: 0, pageSize: Infinity },
    },
  });

  const tableRows = table.getRowModel().rows;
  const tableRowsToDisplay = isViewSelectedOnly
    ? tableRows.filter(row => row.getIsSelected())
    : tableRows;
  const hasDisplayedRows = tableRowsToDisplay.length > 0;

  if (isError || isRefetchError) {
    throw new Error(error?.message);
  }

  return (
    <div
      className={clsx(styles.root, {
        [styles.splitLayout]: shouldReduceColumns,
        [styles.height]: isHistoryTab,
      })}
    >
      {!!tableRowsToDisplay.length && (
        <div
          className={clsx(styles.tableSection, {
            [styles.tableSectionBordered]: isHistoryTab,
          })}
        >
          <EarningsTable
            table={table}
            rows={tableRowsToDisplay}
            onRowClick={onRowClick}
            isHistoryTab={isHistoryTab}
          />
        </div>
      )}
      {shouldReduceColumns && (
        <div
          className={clsx(styles.contentWrapper, {
            [styles.contentWrapperFullWidth]: !hasDisplayedRows,
          })}
        >
          {hasDisplayedRows ? (
            <EarningsPriceMovementContent
              rowSelection={rowSelection}
              entity={entity}
            />
          ) : (
            <ErrorMessageWrapper
              className={styles.emptyState}
              variant="empty"
              displayIcon
              title={t('widget:earnings.empty.title')}
              subTitle={t('widget:earnings.empty.emptyFilteredResultsSubTitle')}
              showBorder={false}
              customIcon={
                <img
                  alt=""
                  className={styles.emptyStateIcon}
                  height={160}
                  src="/app/static/images/empty-state/radio-telescope.png"
                  width={160}
                />
              }
              buttonProps={{
                label: t('widget:earnings.resetAll'),
                onClick: resetAllFilterOptions,
                variant: 'primary',
                size: 'small',
                'data-testid': 'earnings-reset-all-filters-button',
              }}
            />
          )}
        </div>
      )}
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
    </div>
  );
};
